import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { CheckCircle, Download, Loader2, BookOpen, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ShopSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [purchase, setPurchase] = useState(null);
  const [error, setError] = useState(null);

  const sessionId = searchParams.get('session_id');
  const purchaseId = searchParams.get('purchase_id');

  useEffect(() => {
    const verifyPurchase = async () => {
      if (!sessionId) {
        setError('Session de paiement non trouvée');
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/shop/verify-purchase/${sessionId}`);
        setPurchase(response.data);
      } catch (error) {
        console.error('Verification error:', error);
        setError(error.response?.data?.detail || 'Erreur lors de la vérification du paiement');
      } finally {
        setLoading(false);
      }
    };

    verifyPurchase();
  }, [sessionId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex flex-col items-center justify-center py-20">
          <Loader2 className="w-10 h-10 animate-spin text-sage-600 mb-4" />
          <p className="text-muted-foreground">Vérification du paiement...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="container-app py-20">
          <div className="max-w-md mx-auto text-center">
            <div className="w-20 h-20 mx-auto mb-6 bg-red-100 rounded-full flex items-center justify-center">
              <AlertCircle className="w-10 h-10 text-red-600" />
            </div>
            <h1 className="font-heading text-2xl font-semibold mb-4">Erreur de vérification</h1>
            <p className="text-muted-foreground mb-8">{error}</p>
            <Link to="/boutique">
              <Button>Retour à la boutique</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white">
      <Navbar />
      
      <div className="container-app py-20">
        <div className="max-w-lg mx-auto text-center">
          {/* Success Icon */}
          <div className="w-24 h-24 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center animate-bounce-slow">
            <CheckCircle className="w-12 h-12 text-green-600" />
          </div>
          
          <h1 className="font-heading text-3xl font-semibold mb-4 text-green-800">
            Merci pour votre achat !
          </h1>
          
          <p className="text-muted-foreground mb-8">
            Votre paiement a été confirmé. Vous pouvez maintenant télécharger votre livre.
          </p>
          
          {/* Product Info Card */}
          <div className="bg-white rounded-2xl border border-green-200 p-6 mb-8 shadow-soft">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-purple-600" />
              </div>
              <div className="text-left">
                <p className="font-semibold text-lg">{purchase?.product_title}</p>
                <p className="text-sm text-muted-foreground">Format EPUB</p>
              </div>
            </div>
            
            {/* Download Button */}
            <a 
              href={`${API}${purchase?.download_url}`}
              className="block"
            >
              <Button className="w-full h-12 bg-green-600 hover:bg-green-700 text-lg" data-testid="download-btn">
                <Download className="w-5 h-5 mr-2" />
                Télécharger mon livre
              </Button>
            </a>
            
            <p className="text-xs text-muted-foreground mt-4">
              Vous pouvez télécharger ce livre autant de fois que vous le souhaitez.
            </p>
          </div>
          
          {/* Additional Info */}
          <div className="bg-purple-50 rounded-xl p-4 text-sm text-purple-800 mb-8">
            <p className="font-medium mb-1">Comment lire votre livre ?</p>
            <p className="text-purple-600">
              Le fichier EPUB peut être lu sur la plupart des liseuses (Kindle, Kobo), 
              tablettes et applications de lecture (Apple Books, Google Play Livres, Calibre).
            </p>
          </div>
          
          {/* Navigation */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/boutique">
              <Button variant="outline">
                <BookOpen className="w-4 h-4 mr-2" />
                Continuer mes achats
              </Button>
            </Link>
            <Link to="/">
              <Button variant="ghost">
                Retour à l'accueil
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShopSuccessPage;
